<template>
  <footer class="bg-gray-50 dark:bg-dark-surface border-t border-gray-200 dark:border-dark-border">
    <div class="container-custom py-12">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
        <!-- Brand Column -->
        <div class="md:col-span-2">
          <div class="flex items-center space-x-3 mb-4">
            <img
              src="@/assets/logo-icon.webp"
              alt="Devolysis Logo"
              class="h-10 w-10"
            />
            <span class="text-2xl font-display font-bold text-gradient">
              Devolysis
            </span>
          </div>
          <p class="text-gray-600 dark:text-gray-400 mb-4 max-w-md">
            Transforming ideas into digital reality through innovative web development,
            app development, and AI-powered solutions.
          </p>
        </div>

        <!-- Quick Links -->
        <div>
          <h3 class="font-display font-bold text-gray-900 dark:text-gray-100 mb-4">
            Quick Links
          </h3>
          <ul class="space-y-2">
            <li v-for="link in quickLinks" :key="link.label">
              <a
                :href="link.href"
                @click.prevent="scrollToSection(link.href)"
                class="text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-accent transition-colors duration-200 cursor-pointer"
              >
                {{ link.label }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Services -->
        <div>
          <h3 class="font-display font-bold text-gray-900 dark:text-gray-100 mb-4">
            Services
          </h3>
          <ul class="space-y-2">
            <li v-for="service in servicesList" :key="service">
              <span class="text-gray-600 dark:text-gray-400">
                {{ service }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="pt-8 border-t border-gray-200 dark:border-dark-border flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          &copy; {{ currentYear }} Devolysis. All rights reserved.
        </p>

        <div class="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
          <router-link
            to="/privacy-policy"
            class="hover:text-primary dark:hover:text-accent transition-colors duration-200"
          >
            Privacy Policy
          </router-link>
          <router-link
            to="/terms-of-service"
            class="hover:text-primary dark:hover:text-accent transition-colors duration-200"
          >
            Terms of Service
          </router-link>
          <router-link
            to="/faq"
            class="hover:text-primary dark:hover:text-accent transition-colors duration-200"
          >
            FAQ
          </router-link>
        </div>
      </div>
    </div>

    <!-- Back to Top Button -->
    <transition name="fade">
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="fixed bottom-8 right-8 p-3 bg-primary dark:bg-accent text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 z-40"
        aria-label="Scroll to top"
      >
        <ChevronUp :size="24" />
      </button>
    </transition>
  </footer>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { ChevronUp } from 'lucide-vue-next';
import { useSmoothScroll } from '@/composables/useSmoothScroll';

const { scrollToSection, scrollToTop } = useSmoothScroll();

const currentYear = computed(() => new Date().getFullYear());
const showBackToTop = ref(false);


const quickLinks = [
  { label: 'Home', href: '#hero' },
  { label: 'Services', href: '#services' },
  { label: 'Portfolio', href: '#portfolio' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Contact', href: '#contact' }
];

const servicesList = [
  'Website Development',
  'App Development',
  'AI Agents',
  'Consulting'
];

const handleScroll = () => {
  showBackToTop.value = window.scrollY > 300;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
